import { Status, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    type ApiFichiersPostExpediteurEnum,
    type ApiFichiersPostExpeditionEnum,
    type FichiersDossierJsonldDocumentfileRead,
    type FichiersDossierCreatedFilesPostResponseDtoJsonldDocumentfileRead,
    type TblTypeDocumentJsonldTypedocumentdossierRead,
    type TypeDeDocumentDesDossiersApiApiTypesDocDossierPostRequest,
    type TypeDeDocumentDesDossiersJsonldTypedocumentdossierCreate,
    type TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDownload } from '@europrocurement/l2d-modules/hooks';

export type TypeDocument = TblTypeDocumentJsonldTypedocumentdossierRead;
export type DossierDocument = TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead;
export type DossierFichier = FichiersDossierJsonldDocumentfileRead;
export type DossierFichiers = FichiersDossierCreatedFilesPostResponseDtoJsonldDocumentfileRead;
export type DocumentColor = 'primary' | 'error' | 'success' | 'secondary' | 'warning';
export type DocumentExtension = 'pdf' | 'jpg' | 'jpeg' | 'png' | 'doc' | 'docx' | 'xls' | 'xlsx';
export type DocumentIconType =
    | 'file'
    | 'file-pdf'
    | 'file-jpg'
    | 'file-png'
    | 'file-doc'
    | 'file-xls';

const STATUS_TYPE_DOCUMENT = 2;
export const EXPEDITION_PRO = 1;
export const EXPEDITION_API = 5;
export const EXPEDITEUR_PRESCRIPTEUR = 1;
export const EXPEDITEUR_FORMALISTE = 3;

const useDossierDocumentFilesApi = () => {
    const { documentsApi, statusApi, fichiersDossierApi } = useFormaliteService();
    const { download } = useDownload();

    const isFileSupported = (extension: DocumentExtension): boolean =>
        ['pdf', 'jpg', 'png'].includes(extension);

    const getFileIcon = (extension: DocumentExtension): DocumentIconType => {
        switch (extension) {
            case 'pdf':
                return 'file-pdf';
            case 'jpg':
            case 'jpeg':
                return 'file-jpg';
            case 'png':
                return 'file-png';
            case 'doc':
            case 'docx':
                return 'file-doc';
            case 'xls':
            case 'xlsx':
                return 'file-xls';
            default:
                return 'file';
        }
    };

    const getDosDocs = (dossierId: number) =>
        documentsApi
            .apiDossiersDossierTypesDocDossierGetCollection({
                dossier: dossierId as unknown as string,
                itemsPerPage: 100,
            })
            .then((res) => res.data['hydra:member'] as Array<DossierDocument>);

    /**
     * link a document type to a dossier
     * @param dossierId number
     * @param typeDocumentId number documentType id
     * @param libelle string documentType libelle
     * @param dossierStatusId number documentType id
     */
    const postDosDoc = (
        dossierId: number,
        typeDocumentId: number,
        libelle: string,
        dossierStatusId: number,
    ) =>
        documentsApi
            .apiTypesDocDossierPost({
                typeDeDocumentDesDossiersJsonldTypedocumentdossierCreate: {
                    dossier: `/formalite/dossiers/${dossierId}`,
                    document: `/formalite/types-documents/${typeDocumentId}`,
                    libelle,
                    statut: `/formalite/statuts/${dossierStatusId}`,
                    noteFormaliste: '',
                    deleted: false,
                } as TypeDeDocumentDesDossiersJsonldTypedocumentdossierCreate,
            } as TypeDeDocumentDesDossiersApiApiTypesDocDossierPostRequest)
            .then((res) => res.data);

    const getDocsStatuses = () =>
        statusApi
            .apiStatutsGetCollection({ type: STATUS_TYPE_DOCUMENT })
            .then((res) => res.data['hydra:member'] as Array<Status>);

    const getDosDocFiles = (dossierId: number) =>
        fichiersDossierApi
            .apiDossiersDossierFichiersGetCollection({
                dossier: dossierId as unknown as string,
                itemsPerPage: 100,
            })
            .then((res) => res.data['hydra:member'] as Array<DossierFichier>);

    const uploadDosDocFile = (
        dossierId: number,
        typeDoc: string,
        file: File,
        expediteur?: ApiFichiersPostExpediteurEnum,
        expedition?: ApiFichiersPostExpeditionEnum,
        typageAuto?: boolean,
        deliassageAuto?: boolean,
    ) =>
        fichiersDossierApi
            .apiFichiersPost({
                dossier: dossierId as unknown as string,
                typeDoc,
                file,
                expediteur: expediteur || EXPEDITEUR_PRESCRIPTEUR,
                expedition: expedition || EXPEDITION_API,
                typageAuto: typageAuto ?? false,
                deliassageAuto: deliassageAuto ?? true,
            })
            .then((res) => res.data as DossierFichiers);

    const uploadAdditionnalDocDosFile = (
        dossierId: number,
        file: File,
        expediteur?: ApiFichiersPostExpediteurEnum,
        expedition?: ApiFichiersPostExpeditionEnum,
        typageAuto?: boolean,
        deliassageAuto?: boolean,
    ): Promise<DossierFichiers> =>
        fichiersDossierApi
            .apiFichiersPost({
                dossier: dossierId as unknown as string,
                file,
                expediteur: expediteur || EXPEDITEUR_PRESCRIPTEUR,
                expedition: expedition || EXPEDITION_API,
                typageAuto: typageAuto ?? true,
                deliassageAuto: deliassageAuto ?? true,
            })
            .then((res) => res.data as DossierFichiers);

    const removeDosDocFile = (fichierId: number) =>
        fichiersDossierApi.apiFichiersIdDelete({ id: fichierId as unknown as string });

    const updateDosDocFile = (fichierId: number, payload: Record<string, unknown>) => {
        const requestParameter = {
            id: fichierId as unknown as string,
            fichiersDossierUpdateDocumentFileDocumentfileUpdate: {
                typageAuto: false,
                ...payload,
            },
        };

        return fichiersDossierApi.apiFichiersIdPatch(requestParameter);
    };

    const removeDosDoc = (documentId: number) =>
        documentsApi.apiTypesDocDossierIdDelete({ id: documentId as unknown as string });

    // TODO:  Move to an helper file
    const getColorByStatus = (status: 'pending' | 'error' | 'success'): DocumentColor => {
        if (status === 'error') {
            return 'error';
        }

        if (status === 'success') {
            return 'success';
        }

        return 'warning';
    };

    const getDosDocFileContent = (fichierId: number) =>
        fichiersDossierApi.apiFichiersIdFileGet(
            { id: fichierId as unknown as string },
            { responseType: 'blob' },
        );

    const downloadDosDocFile = async (fichier: DossierFichier) => {
        const content = await getDosDocFileContent(fichier.id as number);
        download(fichier.fichier as string, content.data as unknown as Blob);
    };

    const downloadDosDocFilesAsArchive = async (
        fileName: string,
        dossierId: number,
        fileTypes: number[],
    ) => {
        const content = await fichiersDossierApi.apiDossiersDossierFichiersGetCollection(
            {
                dossier: dossierId as unknown as string,
                expediteur2: fileTypes,
            },
            {
                responseType: 'arraybuffer',
                headers: {
                    accept: 'application/zip',
                },
            },
        );

        download(fileName, content.data as unknown as Blob, 'application/zip');
    };

    return {
        // Dossier Documents - DosDoc
        getDosDocs,
        postDosDoc,
        removeDosDoc,

        // Dossier Fichiers DosDocFile
        getDosDocFileContent, // Blob content
        getDosDocFiles,
        uploadDosDocFile,
        uploadAdditionnalDocDosFile,
        updateDosDocFile,
        removeDosDocFile,
        downloadDosDocFile,
        downloadDosDocFilesAsArchive,

        // Document Statuses - DossierStatus
        getDocsStatuses,

        // Helper methods
        getColorByStatus,
        isFileSupported,
        getFileIcon,
    };
};

export default useDossierDocumentFilesApi;
